import {
	type FilteredNavItem,
	filterNavItems,
	getProjectSettingsNav,
	navData,
	organizationNavData,
	organizationSettingsNavData,
} from '@/data/nav-data'
import { useCurrentPlan } from '@/hooks/access-control'
import { useNavScope } from '@/hooks/useNavScope'
import { getNavRouteTarget } from '@/lib/navRoutes'
import { isConsoleUser, selectShowTabs } from '@/stores/auth'
import { useUser } from '@/stores/authStore'
import { isConsoleDeployment } from '@/utils/deployment'
import { useIsAdmin, useIsOwner } from '@/utils/permissions'
import { useRouter } from '@tanstack/react-router'
import { Package, Radio } from 'lucide-react'
import { useMemo } from 'react'
import { type NavCommandEntry, type NavSection, collectNavCommands } from './navigationCommands'
import type { PaletteCommand } from './types'

const ORG_BILLING_ID = 'org-billing'
interface PaletteOnlyPage {
	id: string
	title: string
	path: string
	icon: NonNullable<PaletteCommand['icon']>
	keywords: string[]
	/** Every tab is listed, the default one included, since the page opens on it without saying so. */
	tabs: readonly [key: string, title: string][]
	/** The sidebar page that links here: offered only alongside it, with its plan lock. */
	anchorNavId: string
}

/** Pages with no sidebar entry of their own, reached from a button on a sidebar page. */
const PALETTE_ONLY_PAGES: readonly PaletteOnlyPage[] = [
	{
		id: 'assets',
		title: 'Assets',
		path: '/actions/assets',
		icon: Package,
		keywords: ['Actions', 'asset', 'firmware', 'config'],
		tabs: [
			['files', 'Files'],
			['firmware', 'Firmware'],
			['json', 'JSON'],
			['geofence', 'Geofence'],
		],
		anchorNavId: 'nav-project-remote-commands',
	},
	{
		id: 'session-rules',
		title: 'Session Rules',
		path: '/sessions/rules',
		icon: Radio,
		keywords: ['Sessions', 'rule', 'session'],
		tabs: [
			['all', 'All'],
			['no-signal', 'No Signal'],
			['signal-based', 'Signal-based'],
		],
		anchorNavId: 'nav-project-sessions',
	},
]

function paletteOnlyEntries(
	page: PaletteOnlyPage,
	anchor: NavCommandEntry,
	resolve: (path: string) => NavCommandEntry['target'],
): NavCommandEntry[] {
	const base = {
		description: anchor.description,
		keywords: page.keywords,
		icon: page.icon,
		planLocked: anchor.planLocked,
	}
	return [
		{ ...base, id: `nav-project-${page.id}`, title: `Go to ${page.title}`, target: resolve(page.path), isTab: false },
		...page.tabs.map(([key, title]) => ({
			...base,
			id: `nav-project-${page.id}-${key}`,
			title: `Go to ${page.title}: ${title}`,
			keywords: [...page.keywords, title],
			target: resolve(`${page.path}/${key}`),
			isTab: true,
		})),
	]
}

/**
 * One command per page the sidebar would offer in the current scope. Visibility mirrors `AppSidebar`:
 * show_tabs and plan from `filterNavItems`, show_tabs for project settings too, admin only settings for
 * admins, organization pages for owners, and the console split where console users only get billing. An
 * expired plan leaves only billing. Like the sidebar, a page the role cannot view is still listed: the
 * page itself says access is restricted.
 */
export function useNavigationCommands(isAccessBlocked: boolean): PaletteCommand[] {
	const router = useRouter()
	const user = useUser()
	const navScope = useNavScope()
	const currentPlan = useCurrentPlan()
	const isAdmin = useIsAdmin()
	const isOwner = useIsOwner()

	return useMemo(() => {
		const { orgId, tenantId } = navScope
		const isConsoleOnly = isConsoleDeployment() && isConsoleUser(user)
		const showTabs = selectShowTabs(user)

		const orgScope = { orgId, tenantId: undefined, routeTenantId: undefined }
		const resolveProject = (path: string) => getNavRouteTarget(path, navScope)
		const resolveOrg = (path: string) => getNavRouteTarget(path, orgScope)
		const resolveProjectSettings = (path: string) =>
			getNavRouteTarget(path, { orgId, tenantId, routeTenantId: tenantId })

		const toCommand = (entry: NavCommandEntry): PaletteCommand => ({
			id: entry.id,
			title: entry.title,
			group: 'navigation',
			description: entry.description,
			keywords: entry.keywords,
			icon: entry.icon,
			planLocked: entry.planLocked,
			hiddenUntilSearch: entry.isTab,
			run: () => router.navigate({ to: entry.target.to, params: entry.target.params }),
		})

		const sections: { items: FilteredNavItem[]; section: NavSection; resolve: typeof resolveOrg }[] = []
		if (orgId && tenantId && !isConsoleOnly && !isAccessBlocked) {
			sections.push({
				items: filterNavItems(navData, { currentPlan, showTabs, user }),
				section: 'project',
				resolve: resolveProject,
			})
		}
		// Settings links follow the tenant in the URL, which an org page lacks, so the palette names the
		// fallback project outright rather than landing on the organization's own settings.
		if (orgId && tenantId && !isConsoleOnly && !isAccessBlocked) {
			sections.push({
				items: getProjectSettingsNav(showTabs, user, isAdmin),
				section: 'project-settings',
				resolve: resolveProjectSettings,
			})
		}
		if (orgId && isOwner) {
			const orgItems = organizationNavData.filter(item =>
				isConsoleOnly || isAccessBlocked ? item.id === ORG_BILLING_ID : item.id !== ORG_BILLING_ID,
			)
			sections.push({ items: orgItems, section: 'org', resolve: resolveOrg })
			if (!isConsoleOnly && !isAccessBlocked) {
				sections.push({ items: organizationSettingsNavData, section: 'org-settings', resolve: resolveOrg })
			}
		}

		const entries = sections.flatMap(({ items, section, resolve }) => collectNavCommands(items, section, resolve))
		for (const page of PALETTE_ONLY_PAGES) {
			const anchor = entries.find(entry => entry.id === page.anchorNavId)
			if (anchor) {
				entries.push(...paletteOnlyEntries(page, anchor, resolveProject))
			}
		}
		return entries.map(toCommand)
	}, [router, user, navScope, currentPlan, isAdmin, isOwner, isAccessBlocked])
}
